// Story 7.12 (ADO-911) design refresh (2026-08-14) — the non-invasive Linked
// Product TOAST that opens on a successful add from SEARCH or a LISTING card.
//
// It is the small sibling of `UpsellModal`: same store, same LINKED-products
// content (AC6 — the `related`/ERP leg of `IRecommendationPort`), same AC9
// ("no Linked Products ⇒ nothing opens", enforced in the store). The design
// refresh splits the presentation by the add's ORIGIN — the PDP keeps the full
// focus-trapped modal; search and listing adds get this corner popup so the user
// is not yanked out of the grid they are browsing ("Add them now without leaving
// this page").
//
// DELIBERATELY NOT A DIALOG. Unlike the modal, this is not focus-trapped, has no
// scrim and does not lock body scroll — that is the whole point of "non-invasive".
// It is a labelled `role="region"` landmark with an accessible close, plus a
// window Escape handler for parity with the modal's dismiss affordances. Because
// it never steals focus, it must not announce itself as a live region either
// (a live region holding interactive controls fights assistive tech).
//
// PRESENTATIONAL. Every shared, timing-sensitive concern — the AC9 backstop, the
// depot-scoped card read, dismiss-on-navigation in both orderings, close-on-
// depot-switch and availability population — lives ONCE in the host
// (`UpsellModal`), which passes the settled data down. This component only draws
// it, so the toast and the modal can never drift on that fragile lifecycle.

import { useEffect } from 'react';
import type { RecommendedProduct } from '../../api/recommendations.client';
import { Button, Icon } from '../../design-system';
import { useBasketStore } from '../../basket/useBasketStore';
import { useAddConfirmation } from '../../basket/useAddConfirmation';
import { UNKNOWN_TOTAL } from '../../basket/cartTotals';
import { packCaseLabel } from '../../basket/packCase';
import { formatMoney, type MoneyValue } from '../../shared/money';
import { ProductImage } from '../Browse/ProductImage';
import styles from './UpsellToast.module.css';

export interface UpsellToastProps {
  /** The added product's display name, for the "{product} added to order" badge. */
  readonly productName: string;
  /** The LINKED products to offer, already capped to `MAX_UPSELL_CARDS` by the host. */
  readonly cards: readonly RecommendedProduct[];
  /** Real basket item count. */
  readonly itemCount: number;
  /** The cart value (the lines at their locked prices), or null when
   *  it is not known, shown as UNKNOWN_TOTAL. */
  readonly cartValue: MoneyValue | null;
  /** Dismiss the toast (X / Dismiss / Escape). */
  readonly onClose: () => void;
}

export function UpsellToast({
  productName,
  cards,
  itemCount,
  cartValue,
  onClose,
}: UpsellToastProps) {
  // Escape dismisses, matching the modal — but WITHOUT a focus trap, so the key
  // is caught on the window rather than inside a trapped subtree. Bound only while
  // the toast is mounted (the host mounts it only when open).
  //
  // The toast listens LAST in the bubble chain (window, after every `document`
  // handler), so an Escape aimed at a layer that co-exists with it — the search
  // typeahead most of all, since the toast fires on search adds — would close
  // that layer AND the toast. That breaks the PO rule that the toast closes only
  // on its OWN X / Dismiss / Escape. Any handler that consumes an Escape marks it
  // with `preventDefault()`; the toast honours that claim and ignores it.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !event.defaultPrevented) onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  // A named <section> is implicitly a `region` landmark — no explicit role (that
  // would trip jsx-a11y/no-redundant-roles). Deliberately NOT role=dialog: the
  // toast is non-invasive and never traps focus.
  return (
    <section className={styles.toast} aria-label="Customers also bought">
      <div className={styles.header}>
        <span className={styles.addedBadge}>
          <span className={styles.addedDot} aria-hidden="true" />
          {productName} added to order
        </span>
        <button
          type="button"
          className={styles.close}
          onClick={onClose}
          aria-label="Dismiss customers also bought"
        >
          <Icon name="x" size={18} />
        </button>
      </div>

      <p className={styles.title}>Customers Also Bought</p>
      <p className={styles.subtitle}>Add them now without leaving this page.</p>

      <ul className={styles.rows}>
        {cards.map((item) => (
          <UpsellToastRow key={item.sku} item={item} />
        ))}
      </ul>

      <div className={styles.footer}>
        {/* The design's "€X · N items": the host's cart value (the lines at their locked
            prices, or UNKNOWN_TOTAL while it is not known) and the real count. */}
        <p className={styles.basketSummary}>
          <span data-testid="upsell-basket-value">
            {cartValue === null ? UNKNOWN_TOTAL : formatMoney(cartValue)}
          </span>
          {' · '}
          <span>
            {itemCount} {itemCount === 1 ? 'item' : 'items'} in basket
          </span>
        </p>
        <div className={styles.footerActions}>
          <button type="button" className={styles.dismiss} onClick={onClose}>
            Dismiss
          </button>
          {/* The basket SCREEN is Epic 8 — there is no /basket route yet, so the
              action stays DISABLED (the coming-soon state lives in the aria-label +
              tooltip, and the disabled styling is the visual cue). SECONDARY
              (tinted-blue) with the full "Go to Basket" label per the Figma toast
              (ADO-911 PO review 2026-08-19): the design draws this as a secondary
              button, not the green primary, and spells the label out in full — the
              earlier compact "Basket" + "soon" pill was a narrow-footer workaround.
              The modal keeps its own treatment (Peter: "the modal is correct"). */}
          <Button
            variant="secondary"
            disabled
            title="The basket screen is not available yet"
            aria-label="Go to basket (coming soon)"
            className={styles.basketButton}
          >
            Go to Basket
          </Button>
        </div>
      </div>
    </section>
  );
}

/** One compact linked-product row: thumbnail + name + a per-row Add.
 *  Mirrors `SubstituteAddButton` (the established compact add): `upsell: false`
 *  so adding a linked product from inside the toast cannot stack a second toast,
 *  and the basket store's UNAVAILABLE gate surfaces as the row's inline error. */
function UpsellToastRow({ item }: { item: RecommendedProduct }) {
  // ADO-2249 (BWG feedback, 5 Nov): `{retailUnits} / {packSize}`, as every card.
  const pack = packCaseLabel(item.retailUnits, item.packSize);
  const pending = useBasketStore((s) => s.pending.has(item.sku));
  const error = useBasketStore((s) => s.mutationErrors.get(item.sku) ?? null);
  const { confirmed, confirm } = useAddConfirmation(item.sku, {
    productName: item.name,
    upsellVariant: 'toast',
    upsell: false,
  });

  return (
    <li className={styles.row}>
      <span className={styles.thumb} aria-hidden={item.imageUrl ? undefined : true}>
        <ProductImage src={item.imageUrl} alt={item.name} />
      </span>
      <span className={styles.rowBody}>
        <span className={styles.rowName}>{item.name}</span>
        {pack !== null && <span className={styles.rowPack}>{pack}</span>}
      </span>
      <span className={styles.rowAdd}>
        <Button
          variant="primary"
          className={styles.addButton}
          onClick={() => void confirm()}
          disabled={pending}
          aria-label={`Add ${item.name} to basket`}
        >
          {confirmed ? 'Added ✓' : 'Add'}
        </Button>
        {/* The button's static aria-label wins over its text, so the visual
            "Added ✓" is silent to assistive tech; this polite live region
            announces the confirmation without disturbing the button's name. */}
        <span className={styles.visuallyHidden} role="status">
          {confirmed ? `Added ${item.name} to basket` : ''}
        </span>
        {error && (
          <span className={styles.rowError} role="alert">
            {error}
          </span>
        )}
      </span>
    </li>
  );
}
